import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { Box, Button, Collapse, Divider, FormControl, IconButton, InputLabel, MenuItem, Select, Typography } from '@mui/material'
import { DataGridPremium, GridTreeDataGroupingCell } from '@mui/x-data-grid-premium'
import { CardPartner } from 'api/swagger/definitions/backoffice'
import { prettifyEnumLabel } from 'helpers/CreditOnCardHelpers'
import { useState } from 'react'
import { useCategoryGroups, useGetCountryGroups } from './adapters'
import { CountryGroupTranslationsTable } from './CountryGroupTranslationsTable'
import { CreateCategoryGroupModal } from './CreateCategoryGroupModal'
import { CreateCategoryModal } from './CreateCategoryModal'
import { EditCategoryGroupModal } from './EditCategoryGroupModal'
import { EditCategoryModal } from './EditCategoryModal'
import { EditCountryGroupTranslationsModal } from './EditCountryGroupTranslationsModal'
import { type DataTable, type SpendControlRow, SpendControlTable } from './SpendControlTable'

const cardPartnerExcludeFilter = [CardPartner.ACCOUNTEC, CardPartner.TRIPLETEX, CardPartner.BOLAGERIET]
const createCategoryAllowedPartners: CardPartner[] = [CardPartner.AMPR, CardPartner.DEHAAN]

export const SpendControlPage = () => {
  const [partner, setPartner] = useState<string | undefined>(CardPartner.AMPR)
  const [createModalOpen, setCreateModalOpen] = useState(false)
  const [createGroupModalOpen, setCreateGroupModalOpen] = useState(false)
  const [editingRow, setEditingRow] = useState<SpendControlRow | null>(null)
  const [editingCountryGroupId, setEditingCountryGroupId] = useState<string | null>(null)
  const [countryGroupSectionExpanded, setCountryGroupSectionExpanded] = useState(false)

  const { data: countryGroups } = useGetCountryGroups()
  const editingCountryGroup =
    editingCountryGroupId != null ? (countryGroups?.find((r) => r.countryGroupId === editingCountryGroupId) ?? null) : null

  const partnerOptions = Object.values(CardPartner)
    .filter((partner) => !cardPartnerExcludeFilter.includes(partner))
    .map((partner) => ({ value: partner, label: prettifyEnumLabel(partner) }))
    .sort((a, b) => a.value.localeCompare(b.value))

  const handleEditRow = (row: SpendControlRow) => setEditingRow(row)
  const handleCloseEdit = () => setEditingRow(null)

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        marginTop: 5,
        gap: 2,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography variant="h2">Spend control categories</Typography>
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            alignItems: 'center',
          }}
        >
          <Button
            variant="contained"
            disabled={!createCategoryAllowedPartners.includes(partner as CardPartner)}
            onClick={() => setCreateGroupModalOpen(true)}
          >
            Create category group
          </Button>
          <Button
            variant="contained"
            disabled={!createCategoryAllowedPartners.includes(partner as CardPartner)}
            onClick={() => setCreateModalOpen(true)}
          >
            Create category
          </Button>
          <FormControl>
            <InputLabel id="card-partner">Card partner</InputLabel>
            <Select
              labelId="card-partner"
              id="card-partner"
              value={partner}
              label="Card partner"
              onChange={(event) => setPartner(event.target.value)}
            >
              {partnerOptions.map(({ label, value }) => (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>
      <CreateCategoryGroupModal open={createGroupModalOpen} onClose={() => setCreateGroupModalOpen(false)} partner={partner} />
      <CreateCategoryModal open={createModalOpen} onClose={() => setCreateModalOpen(false)} partner={partner} />
      {editingRow?.path.length === 1 && <EditCategoryGroupModal open={true} onClose={handleCloseEdit} row={editingRow} partner={partner} />}
      {editingRow?.path.length === 2 && <EditCategoryModal open={true} onClose={handleCloseEdit} row={editingRow} partner={partner} />}
      <SpendControlTable slots={{ Table: DataTableComponent }} useRows={useCategoryGroups} partner={partner} onEditRow={handleEditRow} />

      <Divider />

      <Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <IconButton
            size="small"
            onClick={() => setCountryGroupSectionExpanded((prev) => !prev)}
            aria-expanded={countryGroupSectionExpanded}
            aria-label={countryGroupSectionExpanded ? 'Collapse country group translations' : 'Expand country group translations'}
          >
            <ExpandMoreIcon
              sx={{ transform: countryGroupSectionExpanded ? 'rotate(0deg)' : 'rotate(-90deg)', transition: 'transform 0.2s' }}
            />
          </IconButton>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'baseline',
              gap: 1.5,
            }}
          >
            <Typography variant="h2">Country group translations</Typography>
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
              }}
            >
              Shared across all card partners
            </Typography>
          </Box>
        </Box>
        <Collapse in={countryGroupSectionExpanded} timeout="auto" unmountOnExit>
          <Box
            sx={{
              mt: 1,
              width: '50%',
            }}
          >
            <CountryGroupTranslationsTable
              countryGroups={countryGroups}
              onEditCountryGroup={(cg) => setEditingCountryGroupId(cg.countryGroupId)}
            />
          </Box>
        </Collapse>
        {editingCountryGroup != null && (
          <EditCountryGroupTranslationsModal
            open={true}
            onClose={() => setEditingCountryGroupId(null)}
            countryGroupId={editingCountryGroup.countryGroupId}
            countryGroupName={editingCountryGroup.name}
          />
        )}
      </Box>
    </Box>
  )
}

const DataTableComponent: DataTable = ({ columns, loading, rows }) => {
  return (
    <Box>
      <DataGridPremium
        treeData
        getTreeDataPath={(row) => row.path}
        defaultGroupingExpansionDepth={-1}
        rows={rows}
        columns={columns}
        getRowId={(row) => JSON.stringify(row.path)}
        loading={loading}
        disableRowSelectionOnClick
        getRowHeight={() => 'auto'}
        groupingColDef={{
          headerName: 'Name',
          flex: 1,
          minWidth: 300,
          valueGetter: (_value, row) => row.name,
          renderCell: (params) => (
            <Typography
              component="div"
              fontWeight={params.row.path.length === 1 ? 'bold' : 'normal'}
              sx={{
                fontSize: 14,
                py: 1,
                width: '100%',
              }}
            >
              <GridTreeDataGroupingCell {...(params as Parameters<typeof GridTreeDataGroupingCell>[0])} />
            </Typography>
          ),
        }}
        slots={{ noResultsOverlay: () => <Typography>No results, make sure you have selected a partner</Typography> }}
      />
    </Box>
  )
}
